// FieldVision - MarkerListe.tsx
// Sidebar marker-tabel med søgefelt og sorteret liste
//
// SCOPE: Denne komponent bruges KUN i MineMarkerOversigt.tsx
//        Den bruges IKKE i BookingWizard eller andre komponenter.
//
// FIX 2025-12-31: onFieldClick sender CVR-Marknr for unik identifikation
// FIX 2025-12-31 v3: onFieldHover + isSelected bruger CVR-Marknr for unik hover-highlight

import React from 'react';
import MarkerRow from './MarkerRow';

interface Field {
  Id: number;
  Marknr: string;
  CVR: string;
  IMK_areal: number;
  Geometry?: string;
}

interface MarkWithMaps extends Field {
  maps: {
    hasNDVI: boolean;
    hasUkrudt: boolean;
    totalMaps: number;
  };
}

interface MarkerListeProps {
  fields: MarkWithMaps[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onFieldClick: (fieldKey: string) => void;
  onFieldHover?: (marknr: string | null) => void;
  selectedMarknr?: string | null;
  loadingMaps?: boolean;
  onEditField?: (marknr: string) => void;
  onDeleteField?: (marknr: string) => void;
}

const MarkerListe: React.FC<MarkerListeProps> = ({
  fields,
  searchQuery,
  onSearchChange,
  onFieldClick,
  onFieldHover,
  selectedMarknr,
  loadingMaps = false,
  onEditField,
  onDeleteField
}) => {
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className="marker-liste">
      {/* Søgefelt */}
      <form className="search-container" onSubmit={handleSearch}>
        <input
          type="text"
          className="search-input"
          placeholder="Søg på marknavn"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <button type="submit" className="search-btn">
          <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="white" style={{verticalAlign: "middle", marginRight: "4px"}}><path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z"/></svg>
          Søg
        </button>
      </form>

      {/* Tabel header */}
      <div className="table-header">
        <div></div>
        <div className="col-mark">
          MARK
          <span className="sort-arrow">&#8593;</span>
        </div>
        <div className="col-kort">KORT</div>
      </div>

      {/* Marker liste */}
      <div className="marker-rows">
        {fields.length === 0 ? (
          <div className="no-results">
            <p>Ingen marker matcher "{searchQuery}"</p>
          </div>
        ) : (
          fields.map(field => {
            // FIX v3: Generer unik CVR-Marknr key for denne field
            const fieldKey = `${field.CVR}-${field.Marknr}`;

            return (
              <MarkerRow
                key={field.Id || field.Marknr}
                field={field}
                onClick={() => onFieldClick(fieldKey)}
                // FIX v3: Send CVR-Marknr for unik hover-highlight
                onMouseEnter={() => onFieldHover?.(fieldKey)}
                onMouseLeave={() => onFieldHover?.(null)}
                // FIX v3: Sammenlign med CVR-Marknr for korrekt row highlighting
                isSelected={selectedMarknr === fieldKey}
                loadingMaps={loadingMaps}
                onEdit={onEditField ? () => onEditField(field.Marknr) : undefined}
                onDelete={onDeleteField ? () => onDeleteField(field.Marknr) : undefined}
              />
            );
          })
        )}
      </div>

      {/* Antal marker */}
      <div className="marker-count">
        {fields.length} {fields.length === 1 ? 'mark' : 'marker'}
        {loadingMaps && <span className="loading-maps"> (henter kort-info...)</span>}
      </div>

      <style>{`
        .marker-liste {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: white;
        }

        .search-container {
          display: flex;
          gap: 8px;
          padding: 12px 20px;
          background: white;
          border-bottom: 1px solid #e5e7eb;
        }

        .search-input {
          flex: 1;
          padding: 10px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
          color: #374151;
        }

        .search-input:focus {
          outline: none;
          border-color: #16a34a;
          box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.1);
        }

        .search-input::placeholder {
          color: #9ca3af;
        }

        .search-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
        }

        .search-btn:hover {
          background: #2A4C24;
        }

        .search-icon {
          font-size: 14px;
        }

        .table-header {
          display: grid;
          grid-template-columns: 48px 1fr 60px;
          gap: 8px;
          padding: 10px 20px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          font-size: 11px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .col-mark {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .sort-arrow {
          color: #16a34a;
        }

        .col-kort,
        .col-ret,
        .col-slet {
          text-align: center;
        }

        .marker-rows {
          flex: 1;
          overflow-y: auto;
        }

        .no-results {
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .no-results p {
          margin: 0;
          font-size: 14px;
        }

        .marker-count {
          padding: 10px 20px;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          font-size: 12px;
          color: #6b7280;
        }

        .loading-maps {
          color: #16a34a;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default MarkerListe;
